import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

declare global {
  interface Window {
    _hsq?: unknown[][]
  }
}

const HUBSPOT_SCRIPT_ID = 'hs-script-loader'
const HUBSPOT_SCRIPT_SRC = '//js-na3.hs-scripts.com/343541586.js'

// Scripts/elements the loader injects after it runs
const HUBSPOT_INJECTED_SCRIPT_HOSTS = [
  'hs-scripts.com',
  'hs-analytics.net',
  'hs-banner.com',
  'hscollectedforms.net',
  'usemessages.com',
]
const HUBSPOT_INJECTED_ELEMENT_IDS = [
  'hs-eu-cookie-confirmation',
  'hubspot-messages-iframe-container',
]

// Shared across remounts of the layout so page views are not double-tracked
let lastTrackedPath: string | null = null

const appendHubspot = (currentPath: string) => {
  if (document.getElementById(HUBSPOT_SCRIPT_ID)) return

  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.id = HUBSPOT_SCRIPT_ID
  script.async = true
  script.defer = true
  script.src = HUBSPOT_SCRIPT_SRC
  document.body.appendChild(script)

  // The loader tracks the initial page view itself
  lastTrackedPath = currentPath
}

const removeHubspot = () => {
  document.getElementById(HUBSPOT_SCRIPT_ID)?.remove()
  HUBSPOT_INJECTED_SCRIPT_HOSTS.forEach((host) => {
    document
      .querySelectorAll(`script[src*="${host}"]`)
      .forEach((el) => el.remove())
  })
  HUBSPOT_INJECTED_ELEMENT_IDS.forEach((id) => {
    document.getElementById(id)?.remove()
  })
  window._hsq = undefined
  lastTrackedPath = null
}

/**
 * Loads the HubSpot tracking code when `enabled` and removes it otherwise.
 * Also reports SPA route changes as page views
 * (https://developers.hubspot.com/docs/api/events/tracking-code#tracking-in-single-page-applications).
 */
export const useHubspotTracking = (enabled: boolean) => {
  const { pathname, search } = useLocation()
  const currentPath = `${pathname}${search}`

  useEffect(() => {
    if (enabled) {
      appendHubspot(currentPath)
    } else {
      removeHubspot()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  useEffect(() => {
    if (!enabled || lastTrackedPath === currentPath) return

    lastTrackedPath = currentPath
    const hsq = (window._hsq = window._hsq || [])
    hsq.push(['setPath', currentPath])
    hsq.push(['trackPageView'])
  }, [enabled, currentPath])
}
